import { getHigherPriorityObject, formatBiradsRange } from '../utils/utils';
import {
  BI_RADS_KEYS,
  CLASS_MAP,
  MATCHED_PROJECTION_MAP,
  MICROCALC_CLUSTERABLE,
  MODULES,
  PROJECTION_TO_BREAST,
  SIDES,
} from '../constants/constants';

// Shared Helpers
export const getProjectionString = (projection) => {
  if (!projection) return null;
  if (Array.isArray(projection)) return projection[0];
  if (typeof projection === 'string') return projection;
  return projection;
};

export const processPriors = (lesionAllInfo, matchedLesionAllInfo) => {
  const priorsArray = lesionAllInfo?.priors ? Object.values(lesionAllInfo.priors).reverse() : [];
  const matchedPriorsArray = matchedLesionAllInfo?.priors
    ? Object.values(matchedLesionAllInfo.priors).reverse()
    : [];
  const combinedPriorsArray = [...matchedPriorsArray, ...priorsArray];
  const studyIdMap = new Map();

  combinedPriorsArray.forEach((item) => {
    const existingItem = studyIdMap.get(item.study_id);
    if (!existingItem) {
      studyIdMap.set(item.study_id, item);
    } else {
      studyIdMap.set(item.study_id, getHigherPriorityObject(existingItem, item));
    }
  });

  return Array.from(studyIdMap.values()).slice(0, 3);
};

export const formatNestedFinding = (finding) => {
  const proj = Array.isArray(finding.projection) ? finding.projection[0] : finding.projection;
  const finding_class = finding.finding_class;
  const dbIndex = finding.db_index ?? 0;
  const findingId = finding?.finding_id;

  return {
    ...finding,
    projection: [proj, finding_class, dbIndex, findingId],
    isNestedClass: true,
  };
};

function generateFindingId(module_type, db_index, biradsClass, projection) {
  const prefix = module_type === 'opacities' ? 'o' : 'c';
  const classCode = CLASS_MAP[biradsClass] ?? 'x';
  return `${prefix}#${db_index}-${classCode}-${projection}`;
}

//  Step 1: Normalize
function normalizeFindings(customOpacities, customMicrocalc, zoomedProjection) {
  const findings = [];

  MODULES.forEach(({ key, type }) => {
    const moduleData = key === 'customOpacities' ? customOpacities : customMicrocalc;
    if (!moduleData) return;

    Object.keys(SIDES).forEach((side) => {
      const projections = SIDES[side];

      const activeProjections = zoomedProjection
        ? projections.includes(zoomedProjection)
          ? [zoomedProjection]
          : []
        : projections;

      activeProjections.forEach((projection) => {
        const viewObj = moduleData[projection];
        if (!viewObj) return;

        BI_RADS_KEYS.forEach((biradsClass) => {
          const arr = viewObj[biradsClass];
          if (!Array.isArray(arr)) return;

          arr.forEach((finding, db_index) => {
            const finding_class = finding.birads || finding.finding_class || biradsClass;
            const finding_id =
              finding?.finding_id ?? generateFindingId(type, db_index, finding_class, projection);
            findings.push({
              ...finding,
              finding_id,
              projection,
              finding_class,
              db_index,
              breast: PROJECTION_TO_BREAST[projection],
              module_type: type,
              unique_position: [projection, finding_class, db_index],
            });
          });
        });
      });
    });
  });
  return findings;
}

//   Step 2: Match CC/MLO Pairs
function isMmSize(size) {
  return Array.isArray(size) && size.length >= 2 && Number.isFinite(size[0]) && Number.isFinite(size[1]);
}

/** Largest width/height in matched */
function sizePairMaxExtents(s1, s2) {
  if (!isMmSize(s1) && !isMmSize(s2)) return null;
  if (!isMmSize(s1)) return [s2[0], s2[1]];
  if (!isMmSize(s2)) return [s1[0], s1[1]];
  return [Math.max(s1[0], s2[0]), Math.max(s1[1], s2[1])];
}

function formatSizeMm(size) {
  if (!isMmSize(size)) return null;
  return `${size[0].toFixed(1)} x ${size[1].toFixed(1)} mm`;
}

function getMatchedSizeDisplay(ccFinding, mloFinding) {
  const ms = ccFinding?.matched_size;
  if (isMmSize(ms)) {
    return formatSizeMm(ms);
  }
  const combined = sizePairMaxExtents(ccFinding?.size, mloFinding?.size);
  if (combined) {
    return formatSizeMm(combined);
  }
  if (isMmSize(ccFinding?.size)) {
    return formatSizeMm(ccFinding.size);
  }
  if (isMmSize(mloFinding?.size)) {
    return formatSizeMm(mloFinding.size);
  }
  return '\u2014';
}

function maxBiradsClassFromFindings(items) {
  return items.reduce((maxClass, finding) => {
    return maxBiradsClass(maxClass, finding.finding_class);
  }, null);
}

export const processMultiplePriors = (findings = []) => {
  const combinedPriorsArray = findings.flatMap((finding) => {
    if (!finding?.priors) return [];
    return Object.values(finding.priors).reverse();
  });

  const studyIdMap = new Map();

  combinedPriorsArray.forEach((item) => {
    if (!item?.study_id) return;

    const existingItem = studyIdMap.get(item.study_id);

    if (!existingItem) {
      studyIdMap.set(item.study_id, item);
    } else {
      studyIdMap.set(item.study_id, getHigherPriorityObject(existingItem, item));
    }
  });

  return Array.from(studyIdMap.values()).slice(0, 3);
};

function buildMatchedOpacityRowFromLesionId(items) {
  const ccFinding = items.find((f) => f.projection?.includes('cc'));
  const mloFinding = items.find((f) => f.projection?.includes('mlo'));

  const mainFinding = ccFinding || items[0];
  const secondaryFinding = mloFinding || items[1] || items[0];

  return {
    projection: mainFinding.projection,
    finding_class: maxBiradsClassFromFindings(items),
    module_type: 'opacities',
    breast: mainFinding.breast,
    priors: processMultiplePriors(items),
    location: mainFinding.location || secondaryFinding.location,
    object_id: mainFinding.object_id,
    date_first: mainFinding.date_first || secondaryFinding.date_first,
    date_latest: mainFinding.date_latest || secondaryFinding.date_latest,
    matched_size: getMatchedSizeDisplay(mainFinding, secondaryFinding),
    size_change_first: mainFinding.size_change_first || secondaryFinding.size_change_first,
    size_change_latest: mainFinding.size_change_latest || secondaryFinding.size_change_latest,
    growth_category_first: mainFinding.growth_category_first || secondaryFinding.growth_category_first,
    severity_change_first: mainFinding.severity_change_first || secondaryFinding.severity_change_first,
    growth_category_latest: mainFinding.growth_category_latest || secondaryFinding.growth_category_latest,
    severity_change_latest: mainFinding.severity_change_latest || secondaryFinding.severity_change_latest,
    isMatchedMainRow: true,
    matched_lesions: items,
    is_new: items.some((f) => f.is_new),
  };
}

function groupMatchedLesions(findings) {
  if (!Array.isArray(findings) || findings.length === 0) {
    return { matched: [], unmatched: [] };
  }

  const matched = [];
  const unmatched = [];
  const processedIds = new Set();

  findings.forEach((finding) => {
    if (processedIds.has(finding?.finding_id)) return;

    const hasMatch =
      finding.module_type === 'opacities' &&
      finding.match &&
      Array.isArray(finding.match) &&
      finding.match.length >= 2;

    if (!hasMatch) {
      unmatched.push(finding);
      return;
    }

    const findingProj = finding.projection;
    if (!findingProj) {
      unmatched.push(finding);
      return;
    }

    const matchedProjection = MATCHED_PROJECTION_MAP[findingProj];
    if (!matchedProjection) {
      unmatched.push(finding);
      return;
    }

    const matchArray =
      finding.match.length === 2 ? [matchedProjection, finding.match[0], finding.match[1]] : finding.match;

    if (matchedFinding && !processedIds.has(matchedFinding.finding_id)) {
      const ccFinding = findingProj.includes('cc') ? finding : matchedFinding;
      const mloFinding = findingProj.includes('cc') ? matchedFinding : finding;
      matched.push(buildMatchedOpacityRowFromLesionId(ccFinding, mloFinding, matchArray));
      processedIds.add(finding?.finding_id);
      processedIds.add(matchedFinding.finding_id);
    } else {
      unmatched.push(finding);
    }
  });

  return { matched, unmatched };
}

//  Step 3: Cluster by Class
function groupClasses(unmatchedFindings) {
  if (!Array.isArray(unmatchedFindings) || unmatchedFindings.length === 0) {
    return { clusters: [], singles: [] };
  }

  const clusters = [];
  const singles = [];

  const microcalcBuckets = {
    right: { vessel: [], birads2: [], birads3: [] },
    left: { vessel: [], birads2: [], birads3: [] },
  };

  unmatchedFindings.forEach((finding) => {
    const cls = finding.finding_class || finding.class;

    if (finding.module_type === 'microcalc' && MICROCALC_CLUSTERABLE.includes(cls)) {
      const breast = finding.breast || 'left';
      if (microcalcBuckets[breast]?.[cls]) {
        microcalcBuckets[breast][cls].push(finding);
      } else {
        singles.push(finding);
      }
    } else {
      singles.push(finding);
    }
  });

  ['right', 'left'].forEach((breast) => {
    MICROCALC_CLUSTERABLE.forEach((cls) => {
      const items = microcalcBuckets[breast][cls];
      if (items.length > 1) {
        clusters.push({
          breast,
          finding_class: cls,
          module_type: 'microcalc',
          object_id: `${breast}_microcalc_${cls}`,
          boxes: items.length,
          clustered: true,
          nested_items: items,
        });
      } else if (items.length === 1) {
        singles.push(items[0]);
      }
    });
  });

  return { clusters, singles };
}

//Pair opacities that share object_id

function groupOpacityLesionsByLesionId(findings) {
  const matched = [];
  const rest = [];

  const opacityWithLesionId = findings.filter((f) => f.module_type === 'opacities' && f.object_id);

  const nonOpacityOrWithoutLesionId = findings.filter((f) => !(f.module_type === 'opacities' && f.object_id));

  const groups = new Map();

  opacityWithLesionId.forEach((finding) => {
    const key = `${finding.breast}:${finding?.object_id}`;

    if (!groups.has(key)) {
      groups.set(key, []);
    }

    groups.get(key).push(finding);
  });

  groups.forEach((items) => {
    if (items.length > 1) {
      matched.push(buildMatchedOpacityRowFromLesionId(items));
    } else {
      rest.push(items[0]);
    }
  });

  return {
    matched,
    rest: [...nonOpacityOrWithoutLesionId, ...rest],
  };
}

function mergeLesionSummaryIntoRow(row, opacSummaries, microSummaries) {
  if (!row || row.clustered) return row;
  const key = row?.object_id || row?.finding_id;

  if (row.module_type === 'opacities') {
    const summary = key && opacSummaries[key];
    return summary ? { ...row, lesionSummary: summary } : row;
  }

  if (row.module_type === 'microcalc') {
    const summary = key && microSummaries[key];
    return summary ? { ...row, lesionSummary: summary } : row;
  }

  return row;
}

//   breast is right or left
function getBreast(row) {
  if (row.breast) return row.breast;
  const proj = typeof row.projection === 'string' ? row.projection : row.projection?.[0];
  if (proj && proj.startsWith('r')) return 'right';
  return 'left';
}

export function buildTableRows(customOpacities, customMicrocalc, zoomedProjection, isFindingVisible = () => true) {
  const findings = normalizeFindings(customOpacities, customMicrocalc, zoomedProjection).filter(isFindingVisible);

  const { matched, rest } = groupOpacityLesionsByLesionId(findings);
  const { clusters, singles } = groupClasses(rest);

  const rawRows = [...clusters, ...matched, ...singles];
  const opacFmt = formatLesionTable(customOpacities ?? {});
  const microFmt = formatLesionTable(customMicrocalc ?? {});
  const opacSummaries = { ...opacFmt.leftBreast, ...opacFmt.rightBreast };
  const microSummaries = { ...microFmt.leftBreast, ...microFmt.rightBreast };

  const allRows = rawRows.map((row) => {
    if (row.isMatchedMainRow && row.module_type === 'opacities') {
      const key = row?.object_id || row?.finding_id;
      const summary = key != null ? opacSummaries[key] : null;
      return summary ? { ...row, lesionSummary: summary } : row;
    }
    return mergeLesionSummaryIntoRow(row, opacSummaries, microSummaries);
  });

  const rightFindings = [];
  const leftFindings = [];

  allRows.forEach((row) => {
    const breast = getBreast(row);
    if (breast === 'right') {
      rightFindings.push(row);
    } else {
      leftFindings.push(row);
    }
  });

  return { rightFindings, leftFindings };
}

function formatLesionTable(lesionResult) {
  const dataLeft = {};
  const dataRight = {};
  for (const [projection, biradsData] of Object.entries(
    lesionResult && typeof lesionResult === 'object' ? lesionResult : {}
  )) {
    for (const [birads, findings] of Object.entries(biradsData ?? {})) {
      if (birads === 'birads1') continue;
      if (!Array.isArray(findings) || findings.length === 0) continue;
      for (let i = 0; i < findings.length; i++) {
        const finding = findings[i];
        const aggKey = finding?.object_id || finding?.finding_id;
        if (!aggKey) continue;
        if (projection.toLowerCase() === 'lcc' || projection.toLowerCase() === 'lmlo') {
          dataLeft[aggKey] = _formatLesion(dataLeft[aggKey], finding, projection, birads, i);
        } else if (projection.toLowerCase() === 'rcc' || projection.toLowerCase() === 'rmlo') {
          dataRight[aggKey] = _formatLesion(dataRight[aggKey], finding, projection, birads, i);
        } else {
          console.error('Unknown projection:', projection);
        }
      }
    }
  }
  return {
    leftBreast: dataLeft,
    rightBreast: dataRight,
  };
}

function _formatLesion(curLesion, newFinding, projection, birads, findingIndex) {
  const { finding_id, size, box, contours, ...findingData } = newFinding;
  const findingKey = [projection, birads, findingIndex];
  if (!curLesion) {
    return {
      ...findingData,
      findings: [findingKey],
      birads: birads,
      avg_size: size,
    };
  }
  curLesion.findings.push(findingKey);
  curLesion.avg_size = sizePairMaxExtents(curLesion.avg_size, size) ?? curLesion.avg_size ?? size;
  curLesion.birads = maxBiradsClass(curLesion.birads, birads);
  return curLesion;
}

// Highest BI-RADS category when merging views
function maxBiradsClass(a, b) {
  if (!a) return b;
  if (!b) return a;
  const n1 = parseInt(String(a).replace(/\D/g, ''), 10);
  const n2 = parseInt(String(b).replace(/\D/g, ''), 10);
  if (!Number.isFinite(n1) || !Number.isFinite(n2)) {
    return a;
  }
  return n1 >= n2 ? a : b;
}
